import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  Building2, CheckCircle, XCircle, Loader2, RefreshCw, Users,
  Globe, Copy, ExternalLink, Plus, Clock, ChevronDown, ChevronUp, Eye,
  Edit, Trash2, Save
} from "lucide-react";

const STATUS_STYLES = {
  approved: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  pending_approval: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  rejected: "bg-red-500/20 text-red-400 border-red-500/30"
};

export const SponsorManagementPanel = ({ open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newOrg, setNewOrg] = useState({ organization_name: "", admin_name: "", admin_email: "", organization_type: "cdfi", password: "Welcome2026!", welcome_message: "", program_duration_days: 90 });

  const fetchOrgs = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/sponsor/admin/organizations`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { if (open) fetchOrgs(); }, [open]);

  const handleApprove = async (sponsorId) => {
    try {
      const res = await authFetch(`${API}/sponsor/admin/organizations/${sponsorId}/approve`, { method: "PUT" });
      if (res.ok) { toast.success("Sponsor approved! Portal is now live."); fetchOrgs(); }
      else toast.error("Failed to approve");
    } catch (e) { toast.error("Error"); }
  };

  const handleReject = async (sponsorId) => {
    try {
      const res = await authFetch(`${API}/sponsor/admin/organizations/${sponsorId}/reject`, { method: "PUT" });
      if (res.ok) { toast.success("Sponsor rejected"); fetchOrgs(); }
      else toast.error("Failed to reject");
    } catch (e) { toast.error("Error"); }
  };

  const handleEdit = async () => {
    if (!editOrg) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API}/sponsor/admin/organizations/${editOrg.sponsor_id}/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editForm)
      });
      if (res.ok) { toast.success("Sponsor updated"); setEditOrg(null); fetchOrgs(); }
      else toast.error("Failed to update");
    } catch (e) { toast.error("Error updating"); }
    setSaving(false);
  };

  const handleDelete = async (sponsorId, orgName) => {
    if (!window.confirm(`Delete "${orgName}" and its admin account? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`${API}/sponsor/admin/organizations/${sponsorId}`, { method: "DELETE" });
      if (res.ok) { toast.success(`Deleted ${orgName}`); fetchOrgs(); }
      else toast.error("Failed to delete");
    } catch (e) { toast.error("Error deleting"); }
  };

  const handleCreate = async () => {
    if (!newOrg.organization_name || !newOrg.admin_name || !newOrg.admin_email) { toast.error("Fill in all required fields"); return; }
    setCreating(true);
    try {
      const res = await authFetch(`${API}/sponsor/admin/organizations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newOrg)
      });
      if (res.ok) {
        const result = await res.json();
        toast.success(`Sponsor created! Portal: ${result.portal_url}`);
        setShowCreate(false);
        setNewOrg({ organization_name: "", admin_name: "", admin_email: "", organization_type: "cdfi", password: "Welcome2026!", welcome_message: "", program_duration_days: 90 });
        fetchOrgs();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create");
      }
    } catch (e) { toast.error("Error creating sponsor"); }
    setCreating(false);
  };

  const portalUrl = (slug) => `${window.location.origin}/partner/${slug}`;

  if (!open) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="sponsor-management-panel">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#39FF14]" />
            Sponsor Portal Management
          </DialogTitle>
          <DialogDescription>Create, approve, and manage co-branded sponsor portals</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4">
            {/* Stats + Actions */}
            <div className="flex items-center justify-between">
              <div className="flex gap-4 text-sm">
                <span className="text-neutral-500">Total: <strong className="text-neutral-900">{data?.stats?.total || 0}</strong></span>
                <span className="text-neutral-500">Approved: <strong className="text-emerald-600">{data?.stats?.approved || 0}</strong></span>
                <span className="text-neutral-500">Pending: <strong className="text-amber-600">{data?.stats?.pending || 0}</strong></span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setShowCreate(true)} className="bg-neutral-900 text-white hover:bg-neutral-800" data-testid="create-sponsor-btn">
                  <Plus className="w-4 h-4 mr-1" /> Create Sponsor
                </Button>
                <Button size="sm" variant="ghost" onClick={fetchOrgs}><RefreshCw className="w-4 h-4" /></Button>
              </div>
            </div>

            {/* Org List */}
            {(data?.organizations || []).length > 0 ? (data.organizations).map((org) => {
              const isExpanded = expandedId === org.sponsor_id;
              return (
                <Card key={org.sponsor_id} className="border hover:shadow-sm transition-shadow">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : org.sponsor_id)}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-neutral-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-neutral-900">{org.organization_name}</p>
                          <p className="text-xs text-neutral-500">{org.admin_email} &middot; {org.member_count || 0} members</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${STATUS_STYLES[org.status] || STATUS_STYLES.pending_approval}`}>
                          {org.status === "pending_approval" ? "Pending" : org.status === "approved" ? "Approved" : "Rejected"}
                        </Badge>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t space-y-3">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div><span className="text-neutral-500">Type:</span> <span className="text-neutral-900 capitalize">{org.organization_type || "N/A"}</span></div>
                          <div><span className="text-neutral-500">Admin:</span> <span className="text-neutral-900">{org.admin_name}</span></div>
                          <div><span className="text-neutral-500">Created:</span> <span className="text-neutral-900">{org.created_at ? new Date(org.created_at).toLocaleDateString() : "N/A"}</span></div>
                          <div><span className="text-neutral-500">Duration:</span> <span className="text-neutral-900">{org.program_duration_days || 90} days</span></div>
                        </div>

                        {org.status === "approved" && (
                          <div className="bg-neutral-50 rounded-lg p-3">
                            <p className="text-xs text-neutral-500 mb-1">Portal URL</p>
                            <div className="flex items-center gap-2">
                              <code className="text-xs bg-white border rounded px-2 py-1 flex-1 truncate">{portalUrl(org.slug)}</code>
                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(portalUrl(org.slug)); toast.success("URL copied"); }}>
                                <Copy className="w-3 h-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); window.open(portalUrl(org.slug), "_blank"); }}>
                                <ExternalLink className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        )}

                        {org.welcome_message && (
                          <p className="text-xs text-neutral-500 italic">"{org.welcome_message}"</p>
                        )}

                        <div className="flex flex-wrap gap-2 pt-1">
                          {org.status === "pending_approval" && (
                            <>
                              <Button size="sm" onClick={(e) => { e.stopPropagation(); handleApprove(org.sponsor_id); }} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid={`approve-${org.sponsor_id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleReject(org.sponsor_id); }} className="text-red-600 border-red-200 hover:bg-red-50 text-xs" data-testid={`reject-${org.sponsor_id}`}>
                                <XCircle className="w-3 h-3 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          {org.status === "approved" && (
                            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); window.open(portalUrl(org.slug), "_blank"); }} className="text-xs">
                              <Eye className="w-3 h-3 mr-1" /> View Portal
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setEditOrg(org); setEditForm({ organization_name: org.organization_name, organization_type: org.organization_type || "", welcome_message: org.welcome_message || "", program_duration_days: org.program_duration_days || 90 }); }} className="text-xs" data-testid={`edit-${org.sponsor_id}`}>
                            <Edit className="w-3 h-3 mr-1" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleDelete(org.sponsor_id, org.organization_name); }} className="text-red-600 border-red-200 hover:bg-red-50 text-xs" data-testid={`delete-${org.sponsor_id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            }) : (
              <div className="text-center py-12 text-neutral-500">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="mb-2">No sponsor organizations yet</p>
                <Button size="sm" onClick={() => setShowCreate(true)} className="bg-neutral-900 text-white">
                  <Plus className="w-4 h-4 mr-1" /> Create First Sponsor
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Create Sponsor Dialog */}
    <Dialog open={showCreate} onOpenChange={setShowCreate}>
      <DialogContent className="sm:max-w-lg" data-testid="create-sponsor-dialog">
        <DialogHeader>
          <DialogTitle>Create Sponsor Organization</DialogTitle>
          <DialogDescription>Set up a new co-branded portal. The sponsor admin will receive login credentials.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Organization Name *</Label><Input value={newOrg.organization_name} onChange={e => setNewOrg({...newOrg, organization_name: e.target.value})} placeholder="e.g., Metro CDFI" /></div>
            <div>
              <Label>Type</Label>
              <select className="w-full p-2 border rounded-lg text-sm" value={newOrg.organization_type} onChange={e => setNewOrg({...newOrg, organization_type: e.target.value})}>
                <option value="cdfi">CDFI</option>
                <option value="sba_lender">SBA Lender</option>
                <option value="grant_program">Grant Program</option>
                <option value="econ_dev">Economic Development Org</option>
                <option value="foundation">Foundation</option>
                <option value="government">Government Agency</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Admin Name *</Label><Input value={newOrg.admin_name} onChange={e => setNewOrg({...newOrg, admin_name: e.target.value})} placeholder="Contact name" /></div>
            <div><Label>Admin Email *</Label><Input type="email" value={newOrg.admin_email} onChange={e => setNewOrg({...newOrg, admin_email: e.target.value})} placeholder="admin@org.com" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Temp Password</Label><Input value={newOrg.password} onChange={e => setNewOrg({...newOrg, password: e.target.value})} /></div>
            <div><Label>Program Duration (days)</Label><Input type="number" value={newOrg.program_duration_days} onChange={e => setNewOrg({...newOrg, program_duration_days: parseInt(e.target.value) || 90})} /></div>
          </div>
          <div><Label>Welcome Message</Label><Input value={newOrg.welcome_message} onChange={e => setNewOrg({...newOrg, welcome_message: e.target.value})} placeholder="Custom welcome message (optional)" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button onClick={handleCreate} disabled={creating} className="bg-neutral-900 text-white" data-testid="submit-create-sponsor">
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Create & Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Edit Sponsor Dialog */}
    <Dialog open={!!editOrg} onOpenChange={(o) => { if (!o) setEditOrg(null); }}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-sponsor-dialog">
        <DialogHeader>
          <DialogTitle>Edit Sponsor Organization</DialogTitle>
          <DialogDescription>Update {editOrg?.organization_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Organization Name</Label><Input value={editForm.organization_name || ""} onChange={e => setEditForm({...editForm, organization_name: e.target.value})} /></div>
            <div>
              <Label>Type</Label>
              <select className="w-full p-2 border rounded-lg text-sm" value={editForm.organization_type || ""} onChange={e => setEditForm({...editForm, organization_type: e.target.value})}>
                <option value="cdfi">CDFI</option>
                <option value="sba_lender">SBA Lender</option>
                <option value="grant_program">Grant Program</option>
                <option value="econ_dev">Economic Development Org</option>
                <option value="foundation">Foundation</option>
                <option value="government">Government Agency</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div><Label>Welcome Message</Label><Input value={editForm.welcome_message || ""} onChange={e => setEditForm({...editForm, welcome_message: e.target.value})} placeholder="Custom welcome message" /></div>
          <div><Label>Program Duration (days)</Label><Input type="number" value={editForm.program_duration_days || 90} onChange={e => setEditForm({...editForm, program_duration_days: parseInt(e.target.value) || 90})} /></div>
          <div><Label>Logo URL (optional)</Label><Input value={editForm.logo_url || ""} onChange={e => setEditForm({...editForm, logo_url: e.target.value})} placeholder="https://..." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setEditOrg(null)}>Cancel</Button>
          <Button onClick={handleEdit} disabled={saving} className="bg-neutral-900 text-white" data-testid="save-edit-sponsor">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
